import React from 'react';
import { MemberType } from '@/types/member';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MemberTypeCircleBadgeProps {
  type: MemberType;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary';
}

const getTypeConfig = (type: MemberType): { letter: string; label: string; bgClass: string } => {
  const configs: Record<MemberType, { letter: string; label: string; bgClass: string }> = {
    entrepreneur: { letter: 'E', label: 'Entrepreneur', bgClass: 'bg-orange-500' },
    startup: { letter: 'S', label: 'Startup', bgClass: 'bg-green-500' },
    investisseur: { letter: 'I', label: 'Investisseur', bgClass: 'bg-blue-500' },
    expert: { letter: 'X', label: 'Expert', bgClass: 'bg-purple-500' },
    consultant: { letter: 'C', label: 'Consultant', bgClass: 'bg-yellow-500' },
    membre: { letter: 'M', label: 'Membre', bgClass: 'bg-indigo-500' },
    chercheur: { letter: 'R', label: 'Chercheur', bgClass: 'bg-violet-600' },
    journaliste: { letter: 'J', label: 'Journaliste', bgClass: 'bg-red-500' },
    influenceur: { letter: 'F', label: 'Influenceur', bgClass: 'bg-pink-500' },
    gouvernement: { letter: 'G', label: 'Gouvernement', bgClass: 'bg-gray-600' },
    association: { letter: 'A', label: 'Association', bgClass: 'bg-cyan-500' },
    autre: { letter: '?', label: 'Autre', bgClass: 'bg-gray-400' },
  };
  
  return configs[type] || configs.autre;
};

export const MemberTypeCircleBadge: React.FC<MemberTypeCircleBadgeProps> = ({ 
  type, 
  size = 'md',
  variant = 'primary' 
}) => {
  const { letter, label, bgClass } = getTypeConfig(type);
  
  const sizeClasses = {
    sm: 'w-5 h-5 text-[10px]',
    md: 'w-6 h-6 text-xs',
    lg: 'w-7 h-7 text-sm',
  };
  
  const variantClasses = {
    primary: 'opacity-100 border-2 border-white shadow-md',
    secondary: 'opacity-60 border border-white/50',
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              'rounded-full flex items-center justify-center font-bold text-white',
              'transition-all duration-200 hover:scale-110',
              bgClass,
              sizeClasses[size],
              variantClasses[variant]
            )}
          >
            {letter}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
